import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Divider, Grid, IconButton, List, ListItem, ListItemText, Stack, Typography, styled, useMediaQuery, useTheme } from '@mui/material';
import { COPY } from '../constants';
import { useUser } from '../hooks/useUser';
import { useAgency } from '../hooks/useAgency';
import { useLogout } from '../hooks/useLogout';
import { adminDashboardRoles, verificationRoles, surepassAdminDashboardRoles } from '../utils/authorization';
import ProtectedElement from '../auth/ProtectedElement';
import { isFeatureFlagEnabled } from '../utils/featureFlagUtils';
import { FEATURE_FLAGS, PAGE_PATHS, ROLES } from '../constants';
import { OfficialAvatar } from '@components';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupIcon from '@mui/icons-material/Group';
import StarIcon from '@mui/icons-material/Star';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import placeholderLogo from '../assets/navBarPlaceholder.svg';
import { SidebarIcon } from '../assets/icons/icons';
import Utils from '../utils/utils';

const StyledNavLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.action.active,
  fontSize: '0.875rem',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '8px 19px'
}));

interface NavItemProps {
  to: string;
  label: string;
  icon: JSX.Element;
}

const Sidebar = ({ open, toggleDrawer }: {
  open: boolean;
  toggleDrawer: () => void;
}) => {
  const theme = useTheme();
  const { user } = useUser();
  const { agency } = useAgency();
  const navigate = useNavigate();
  const handleLogout = useLogout();

  const isSmallScreen = useMediaQuery('(max-width:1024px)');
  const isOperationsEnabled = user?.profile && isFeatureFlagEnabled(user.profile, FEATURE_FLAGS.OPERATIONS)
  const isVerificationsEnabled = user?.profile && isFeatureFlagEnabled(user.profile, FEATURE_FLAGS.VERIFICATIONS)

  const NavItem = ({ to, label, icon }: NavItemProps) => {
    const theme = useTheme();
    const location = useLocation();
    const isSelected = location.pathname === to;

    return (
      <ListItem
        key={label}
        component="li"
        sx={{
          backgroundColor: isSelected ? theme.palette.blueGrey[50] : 'transparent',
          padding: 0,
          margin: 0,
        }}>
        <StyledNavLink to={to} onClick={() => {
          if (open && isSmallScreen) {
            toggleDrawer();
          }
          }}>
          {icon}
          <ListItemText
            primary={label}
            sx={{
              marginLeft: '4px',
              color: theme.palette.text.primary,
              opacity: open ? 1 : 0,
              width: open ? 'auto' : 0,
              overflow: 'hidden',
              transition: theme => theme.transitions.create(['opacity', 'width']),
            }}
          />
        </StyledNavLink>
      </ListItem>
    );
  };

  const goHome = () => {
    navigate(PAGE_PATHS.home);
    if (isSmallScreen) {
      toggleDrawer();
    }
  };

  if (!user || !user.profile) {
    return null;
  }

  return (
    <nav aria-label='SurePass Main Navigation' style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Grid
        container
        sx={{
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          paddingTop: '12px',
          paddingRight: '12px',
          transition: theme => theme.transitions.create(['padding-right'])
        }}>
        <IconButton onClick={toggleDrawer} aria-label='Navigation drawer control'>
          <SidebarIcon />
        </IconButton>
      </Grid>
      <Grid
        container
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: open ? 3 : 2,
          marginBottom: open ? 3 : 2,
          transition: theme => theme.transitions.create(['margin-top', 'margin-bottom'])
        }}>
        <Box
          role='button'
          tabIndex={0}
          aria-label='Go to home'
          onClick={goHome}
          onKeyDown={(event) => {
            // A div with role='button' doesn't activate on Enter/Space natively,
            // so we replicate native button keyboard behavior.
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              goHome();
            }
          }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            aspectRatio: '5/3',
            width: open ? '243px' : '44px',
            borderRadius: open ? '10px' : '4px',
            overflow: 'hidden',
            cursor: 'pointer',
            transition: theme => theme.transitions.create(['width', 'height', 'border-radius']),
          }}
        >
          <Box
            component="img"
            src={agency?.logo_url || placeholderLogo}
            alt="Agency Logo"
            sx={{
              height: '100%',
              width: '100%',
              margin: 'auto',
              objectFit: 'contain',
              transition: theme => theme.transitions.create(['padding']),
            }}
          />
        </Box>
        {open && <Typography
          align='center'
          variant='bodyMedium'
          sx={{
            fontWeight: 500,
            marginTop: 2
          }}>
          {user.profile.agency.name}
        </Typography>}
      </Grid>
      <Grid
        container
        sx={{
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
          paddingBottom: '20px'
        }}>
        <Box>
          <List sx={{ padding: 0 }}>
            <ProtectedElement userProfile={user.profile} allowedRoles={adminDashboardRoles}>
              <NavItem
                to={PAGE_PATHS.activity}
                label={COPY.titles.activity}
                icon={<TimelineOutlinedIcon />}
              />
              <NavItem
                to={PAGE_PATHS.checkpoints}
                label={COPY.titles.checkpoints}
                icon={<LocationOnIcon />}
              />
            </ProtectedElement>
            <ProtectedElement userProfile={user.profile} allowedRoles={[...adminDashboardRoles, ROLES.OFFICIAL]}>
              {isOperationsEnabled && (
                <NavItem
                  to={PAGE_PATHS.operations}
                  label={COPY.titles.operations}
                  icon={<StarIcon />}

                />
              )}
            </ProtectedElement>
            <ProtectedElement userProfile={user.profile} allowedRoles={adminDashboardRoles}>
              <NavItem
                to={PAGE_PATHS.users}
                label={COPY.titles.users}
                icon={<GroupIcon />}
              />
            </ProtectedElement>
            <ProtectedElement userProfile={user.profile} allowedRoles={surepassAdminDashboardRoles}>
              <NavItem
                to={PAGE_PATHS.surepassAdmin}
                label={COPY.titles.surepassAdmin}
                icon={<WarningAmberIcon />}
              />
            </ProtectedElement>
            <ProtectedElement
              userProfile={user.profile}
              allowedRoles={[...adminDashboardRoles, ...verificationRoles]}>
              {isVerificationsEnabled && (
                <NavItem
                  to={PAGE_PATHS.verifications}
                  label={COPY.titles.officialVerifications}
                  icon={<VerifiedUserIcon />}

                />
              )}
            </ProtectedElement>
          </List>
          <Divider />
          <List sx={{ padding: 0 }}>
            <ProtectedElement userProfile={user.profile} allowedRoles={adminDashboardRoles}>
              <NavItem
                to={PAGE_PATHS.settings}
                label={COPY.titles.settings}
                icon={<SettingsIcon />}
              />
            </ProtectedElement>
          </List>
        </Box>
        <Box
          sx={{
            pl: open ? 3 : 2,
            marginTop: isSmallScreen ? 2 : 0
          }}
        >
          <Grid
            container
            sx={{
              gap: 1,
              alignItems: 'flex-start'
            }}>
            <OfficialAvatar size='medium' img_url={user.profile.img_url} alt='User profile photo' />
            {open && (
              <Stack direction='column' sx={{
                mt: '-4px'
              }}>
                <Typography>{Utils.fullName(user.profile.first_name, user.profile.last_name)}</Typography>
                <Button
                  variant='text'
                  onClick={() => handleLogout()}
                  sx={{
                    textTransform: 'none',
                    padding: 0,
                    textAlign: 'left',
                    display: 'inline',
                    color: theme.palette.text.secondary,
                    fontSize: '14px',
                    lineHeight: '20px',
                  }}
                >
                  Log Out
                </Button>
              </Stack>
            )}
          </Grid>
          {open && (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, marginTop: 3 }}>
              {COPY.copyrightFooter}
            </Typography>
          )}
        </Box>
      </Grid>
    </nav>
  );
};

export default Sidebar;
